import type { PolicyConfigurationListFilters } from './BenefitPolicyService'

/**
 * The list screen's year tabs.
 *
 * The tab is only ever a **filter** — it picks which rows are shown. Every value rendered in a row
 * (the display state chip, the period, whether the row can be cloned) comes back decided by the
 * server, so nothing here interprets a date beyond bucketing it.
 *
 * Between them the three tabs cover **every** plan year, which is the property that matters: a
 * configuration outside `currentYear` and `currentYear + 1` would otherwise exist and be
 * unreachable, since nothing in the UI could ask for its year. Only `current` maps to a single
 * year the endpoint can filter on; the other two are open-ended ranges, so they read the whole
 * collection and partition it here.
 */
export type PlanYearTabKey = 'current' | 'next' | 'historical'

interface PlanYearTab {
  key: PlanYearTabKey
  label: string
  /** Filters sent to the list endpoint for this tab. */
  filters: PolicyConfigurationListFilters
}

export const planYearTabs = (currentYear: number): PlanYearTab[] => [
  { key: 'current', label: `${currentYear}`, filters: { plan_year: currentYear } },
  // `+` because this is every future plan year, not just the next one: a renewal cloned from next
  // year's policy lands two years out, and it has to be visible somewhere.
  { key: 'next', label: `${currentYear + 1}+`, filters: {} },
  { key: 'historical', label: 'Historical', filters: {} },
]

const planYearOf = (effectiveStart: string | null) =>
  effectiveStart == null ? null : new Date(effectiveStart).getUTCFullYear()

/** Ended plan years — everything before the current one. */
export const isHistorical = (effectiveStart: string | null, currentYear: number) => {
  const year = planYearOf(effectiveStart)
  return year != null && year < currentYear
}

/** Every plan year after the current one, however far out. */
export const isUpcoming = (effectiveStart: string | null, currentYear: number) => {
  const year = planYearOf(effectiveStart)
  return year != null && year > currentYear
}
